import { useState } from 'react'
import { useDispatch } from 'react-redux'
import cn from 'clsx'

// Actions
import { actions as profileActions } from 'modules/profile/actions'

// Components
import { Button, IconCreditCard, IconPayPalCard, Radio } from '@tracklib/kit'

// Hooks
import { useRequest } from 'modules/requests/hooks'
import { useSavedPaymentMethod } from 'modules/pay/hooks'

// Types
import { AvailableCreditCardsData, PayPalData } from 'types'

// Utils
import { niceName, renderCreditCardIcon } from 'components/profile/currentplan'

import styles from './paymentmethods.css'

export type PaymentMethodTypeId = '' | 'saved' | 'card' | 'payPal'

type PaymentMethodType = {
  name: string
  subText: string | null | undefined
  icon: JSX.Element
  type: string
  subTextWarning?: boolean
}

type PaymentMethodProps = PaymentMethodType & {
  setSelectedPaymentMethodId: (id: PaymentMethodTypeId) => void
  selectedId: PaymentMethodTypeId
  removePaymentMethod?: () => void
}

const PaymentMethod = ({
  setSelectedPaymentMethodId,
  name,
  type,
  subText,
  icon,
  selectedId,
  subTextWarning,
  removePaymentMethod,
}: PaymentMethodProps): JSX.Element => {
  const removePaymentMethodRequest = useRequest('REMOVE_SAVED_PAYMENT_METHOD')
  return (
    <Radio
      size="large"
      className={styles.selectBox}
      id={type}
      value={selectedId}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedPaymentMethodId(e.target.value as PaymentMethodTypeId)}
      datatesting={`radio_paymentmethod_${type}`}
    >
      <span className={styles.selectBoxInside}>
        <span className={styles.info}>
          <span>
            <h4>{name}</h4>
            {subText && <p className={cn(subTextWarning && styles.subTextWarning)}>{subText}</p>}
          </span>
        </span>
        <div className={styles.iconWrapper}>
          {icon || (
            <img style={{ height: 22 }} src={`https://checkoutshopper-test.adyen.com/checkoutshopper/img/pm/${type}@2x.png`} alt="" />
          )}
        </div>
      </span>
      {removePaymentMethod && (
        <Button
          className={styles.removeButton}
          variant="transparent"
          onClick={removePaymentMethod}
          disabled={removePaymentMethodRequest.isLoading}
        >
          Remove payment method
        </Button>
      )}
    </Radio>
  )
}

type PaymentMethodsProps = {
  selectedPaymentMethodId: PaymentMethodTypeId
  setSelectedPaymentMethodId: (methodId: PaymentMethodTypeId) => void
  hideRemoveButton?: boolean
  payPalData?: PayPalData
  creditCardsData?: AvailableCreditCardsData
  alwaysExpanded?: boolean
}

const PaymentMethods = ({
  setSelectedPaymentMethodId,
  selectedPaymentMethodId,
  hideRemoveButton,
  payPalData,
  creditCardsData,
  alwaysExpanded,
}: PaymentMethodsProps): JSX.Element => {
  const dispatch = useDispatch()
  const [showOtherPaymentMethods, setShowOtherPaymentMethods] = useState(false)
  const savedPaymentMethod = useSavedPaymentMethod()
  const isPaypal = savedPaymentMethod?.variant === 'paypal'
  const parsedSavedPaymentMethod = savedPaymentMethod && {
    name: isPaypal ? 'PayPal' : `${niceName(savedPaymentMethod.variant)} ending with ${savedPaymentMethod.card_number}`,
    type: 'saved',
    subText: isPaypal
      ? savedPaymentMethod.customer_name
      : `${savedPaymentMethod.expired ? 'Expired' : 'Expires'}: ${savedPaymentMethod.exp_month}/${savedPaymentMethod.exp_year}`,
    subTextWarning: savedPaymentMethod.expired,
    icon: isPaypal ? <IconPayPalCard /> : renderCreditCardIcon(savedPaymentMethod.variant),
    removeable: true,
  }

  const paypalMethod = {
    name: 'Connect new PayPal account',
    type: 'payPal',
    subText: null,
    icon: <IconPayPalCard />,
  }
  const newCardMethod = {
    name: 'Add new card',
    type: 'card',
    subText: `Pay with ${creditCardsData?.brands.map(uglyName => niceName(uglyName)).join(', ')}`,
    icon: <IconCreditCard className={styles.iconAddNew} />,
  }

  const otherPaymentMethods = [...(creditCardsData ? [newCardMethod] : []), ...(payPalData ? [paypalMethod] : [])]
  const showOnlySavedCard = parsedSavedPaymentMethod && !showOtherPaymentMethods && !alwaysExpanded
  const allMethods: Array<PaymentMethodType & { removeable?: boolean }> = [
    ...(parsedSavedPaymentMethod ? [parsedSavedPaymentMethod] : []),
    ...(showOnlySavedCard ? [] : otherPaymentMethods),
  ]

  return (
    <div className={styles.paymentMethods}>
      {allMethods.map(({ name, type, subText, icon, subTextWarning, removeable }, index) => (
        <PaymentMethod
          key={String(index)}
          name={name}
          type={type}
          subText={subText}
          subTextWarning={subTextWarning}
          icon={icon}
          selectedId={selectedPaymentMethodId}
          setSelectedPaymentMethodId={setSelectedPaymentMethodId}
          removePaymentMethod={
            removeable && !hideRemoveButton
              ? () => {
                  if (confirm('Are you sure you want to remove this payment method?')) {
                    dispatch(profileActions.removeSavedPaymentMethod())
                  }
                }
              : undefined
          }
        />
      ))}
      {showOnlySavedCard && (
        <Button variant="transparent" className={styles.anotherMethodButton} onClick={() => setShowOtherPaymentMethods(true)}>
          Change payment method
        </Button>
      )}
    </div>
  )
}

export default PaymentMethods
